import PropTypes from "prop-types";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Title } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Title);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart) {
    const { centerValue, centerLabel } = chart.config.options.plugins.centerText || {};
    if (centerValue === undefined && !centerLabel) return;

    const { ctx, chartArea } = chart;
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();

    if (centerValue !== undefined) {
      ctx.font = "700 22px sans-serif";
      ctx.fillStyle = "#111827";
      ctx.textAlign = "center";
      ctx.textBaseline = centerLabel ? "bottom" : "middle";
      ctx.fillText(String(centerValue), x, y);
    }

    if (centerLabel) {
      ctx.font = "400 11px sans-serif";
      ctx.fillStyle = "#6b7280";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(centerLabel, x, centerValue !== undefined ? y + 2 : y);
    }

    ctx.restore();
  },
};

ChartJS.register(centerTextPlugin);

const DEFAULT_COLORS = ["#06d6a0", "#ffd166", "#ef476f", "#118ab2", "#8338ec", "#ff6b6b", "#4cc9f0", "#f72585", "#4361ee", "#2a9d8f"];

const DonutSkeleton = () => (
  <div className="grid grid-cols-12 px-2 w-full h-full max-w-[700px] mx-auto items-center animate-pulse">
    <div className="col-span-7 flex items-center justify-center">
      <div className="relative w-32 h-32 rounded-full bg-gray-200">
        <div className="absolute inset-4 rounded-full bg-gray-50" />
      </div>
    </div>
    <div className="col-span-5 flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-gray-200 flex-shrink-0" />
          <div className="h-3 rounded bg-gray-200" style={{ width: `${60 + i * 10}%` }} />
        </div>
      ))}
    </div>
  </div>
);

const DonutChart = ({ data, labels, title, cutout, backgroundColors, borderColors, width, height, loading, rounded, centerValue, centerLabel, donutWidth, colors }) => {
  if (loading) return <DonutSkeleton />;
  if (!Array.isArray(data) || !Array.isArray(labels)) {
    console.error("DonutChart: `data` and `labels` must be arrays.");
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
          <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <span className="text-gray-400 text-sm font-medium">Invalid data format</span>
        </div>
        <div className="flex justify-end">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </div>
      </>
    );
  }

  if (data.length !== labels.length) {
    console.error("DonutChart: `data` and `labels` length must match.");
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
          <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <span className="text-gray-400 text-sm font-medium">Data and labels length mismatch</span>
        </div>
        <div className="flex justify-end">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </div>
      </>
    );
  }

  if (!data.length) {
    console.error("DonutChart: `data` and `labels` must be arrays.");
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
          <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <span className="text-gray-400 text-sm font-medium">No Data Found</span>
        </div>
      </>
    );
  }

  // const resolvedColors = backgroundColors?.length >= data.length ? backgroundColors : colors?.length >= data.length ? colors : DEFAULT_COLORS;

  const resolvedColors = data.map((_, idx) => {
    const pool = colors ?? [];
    return pool[idx] ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
  });

  const resolvedBorderColors = borderColors?.length === data.length ? borderColors : Array(data.length).fill("#fff");

  let resolvedCutout = cutout || "70%";
  if (donutWidth !== undefined) {
    if (typeof donutWidth === "number") {
      resolvedCutout = `${100 - donutWidth}%`;
    } else if (typeof donutWidth === "string" && donutWidth.endsWith("%")) {
      resolvedCutout = `${100 - parseFloat(donutWidth)}%`;
    } else {
      resolvedCutout = donutWidth;
    }
  }

  const chartData = {
    labels,
    datasets: [
      {
        data,
        backgroundColor: resolvedColors,
        borderColor: resolvedBorderColors,
        borderWidth: 1,
        borderRadius: rounded ? 6 : 0,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: resolvedCutout,
    plugins: {
      legend: { display: false },
      title: { display: !!title, text: title },
      centerText: { centerValue, centerLabel },
    },
  };

  return (
    <div className="grid grid-cols-12 !px-2 w-full h-full max-w-[700px] mx-auto items-center">
      <div className="col-span-7 mx-auto" style={{ width: width || "90%", height: height || "90%" }}>
        <Doughnut data={chartData} options={options} />
      </div>

      <div className="col-span-5 flex flex-col gap-3 text-xs">
        {labels.map((label, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: resolvedColors[idx] || "#fff" }} />
            <span className=" font-medium w-36 text-left">{label}</span>
            <span className=" font-semibold">{data[idx]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

DonutChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string,
  cutout: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  backgroundColors: PropTypes.arrayOf(PropTypes.string),
  borderColors: PropTypes.arrayOf(PropTypes.string),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  rounded: PropTypes.bool,
  centerValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  centerLabel: PropTypes.string,
  donutWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  colors: PropTypes.arrayOf(PropTypes.string),
};

DonutChart.defaultProps = {
  rounded: false,
  loading: false,
};

export default DonutChart;
